import React, { useEffect, useState } from "react";
import { Plus, Pencil, Archive, Undo2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Admin-only card for managing the Project/Service Type dropdown values.
 * BD users see only `is_active=true` entries when they open the Projects form.
 * Historical values are never deleted — archived types stay on old projects.
 */
export default function ServiceTypesCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/service-types");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      await api.post("/api/service-types", { name: n });
      setNewName("");
      toast.success("Service type added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const rename = async (id) => {
    const n = editName.trim();
    if (!n) return;
    try {
      await api.patch(`/api/service-types/${id}`, { name: n });
      setEditingId(null); setEditName("");
      toast.success("Renamed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleActive = async (item) => {
    try {
      await api.patch(`/api/service-types/${item.id}`, { is_active: !item.is_active });
      toast.success(item.is_active ? "Archived" : "Restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="service-types-card">
      <div className="mb-3">
        <h3 className="font-heading font-medium text-slate-800">Service / Project Types</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Values that appear in the "Service Type" dropdown when creating a project.
          Archive (do not delete) obsolete types so historical projects keep their tag.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Input
          data-testid="new-service-type-input"
          placeholder="e.g. Millwork Shop Drawings"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <Button onClick={add} data-testid="add-service-type-btn" className="bg-brand hover:bg-brand-hover">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      {loading && <div className="text-xs text-slate-400">Loading…</div>}

      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.id}
              data-testid={`service-type-row-${it.id}`}
              className={`flex items-center gap-2 py-2 ${it.is_active ? "" : "opacity-60"}`}
          >
            {editingId === it.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && rename(it.id)} autoFocus />
                <Button size="sm" onClick={() => rename(it.id)} data-testid={`save-st-${it.id}`}>
                  <Save className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditName(""); }}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-slate-800">
                  {it.name}
                  {!it.is_active && <span className="ml-2 text-[10px] uppercase text-slate-400">archived</span>}
                </span>
                <Button size="sm" variant="ghost"
                        onClick={() => { setEditingId(it.id); setEditName(it.name); }}
                        data-testid={`edit-st-${it.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={() => toggleActive(it)}
                        data-testid={`toggle-st-${it.id}`}
                        title={it.is_active ? "Archive" : "Restore"}>
                  {it.is_active ? <Archive className="w-3.5 h-3.5 text-amber-600" /> : <Undo2 className="w-3.5 h-3.5 text-emerald-600" />}
                </Button>
              </>
            )}
          </li>
        ))}
        {!loading && !items.length && (
          <li className="text-xs text-slate-400 py-3 text-center">No service types yet — add one above.</li>
        )}
      </ul>
    </div>
  );
}
